/**
 * Component: Modal
 * ------------------------------------------------------------------------------
 * Instantiate Micromodal and configure defaults across modal components.
 * - https://micromodal.now.sh/#configuration
 *
 * @namespace Modal
 */
import * as a11y from '@shopify/theme-a11y';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { updateTabindexOnElement } from '~/helpers/a11y';
import { extendDefaults } from '~/helpers/utils';
import MicroModal from '~/vendors/micromodal';
/**
 * Instance events.
 */

const eventSelector = {
  modalClose: 'Modal:Close',
  modalShow: 'Modal:Show'
};
/**
 * Create a new modal object.
 * @param {object} config - Settings for modal instance.
 */

export default (config => {
  /**
   * Instance globals.
   */
  const modal = MicroModal;
  /**
   * Instance default settings.
   */

  const defaults = {
    awaitCloseAnimation: true
  };
  /**
   * Instance settings.
   */

  const settings = extendDefaults(defaults, config);
  /**
   * Instantiate a new modal.
   */

  function init() {
    modal.init({
      onShow: container => onShowCallback(container.id),
      onClose: container => onCloseCallback(container.id)
    });
    unsetTabIndexOnContainer(settings.id);
    setEventBusListeners();
  }
  /**
   * Set event bus listeners.
   */


  function setEventBusListeners() {
    Frame.EventBus.listen("".concat(eventSelector.modalShow, ":").concat(settings.id), element => {
      window._previousFocus = document.activeElement;
      updateTabindexOnElement(element, 0);
      disableBodyScroll(element);
    });
    Frame.EventBus.listen("".concat(eventSelector.modalClose, ":").concat(settings.id), element => {
      window._previousFocus.focus();

      updateTabindexOnElement(element, -1);
      enableBodyScroll(element);
    });
  }
  /**
   * Force show the modal instance.
   * @param {String} id - Modal container id.
   */


  function show(id) {
    modal.show(id);
  }
  /**
   * Force close the modal instance.
   * @param {String} id - Modal container id.
   */


  function close(id) {
    modal.close(id);
  }
  /**
   * Show modal with default settings.
   * @param {String} id - Modal container id.
   */


  function onShowCallback(id) {
    setTabIndexOnContainer(id);
  }
  /**
   * Close modal with default settings.
   * @param {String} id - Modal container id.
   */


  function onCloseCallback(id) {
    unsetTabIndexOnContainer(id);
  }
  /**
   * Unset tabindex to 0 to make them un-tabbable.
   * @param {String} id - Modal container id.
   */


  function unsetTabIndexOnContainer(id) {
    const focusable = a11y.focusable(getContainerById(id));
    focusable.forEach(element => element.setAttribute('tabindex', -1));
  }
  /**
   * Unset tabindex to 1 to make them tabbable.
   * @param {String} id - Modal container.
   */


  function setTabIndexOnContainer(id) {
    const focusable = a11y.focusable(getContainerById(id));
    focusable.forEach(element => element.setAttribute('tabindex', 0));
  }
  /**
   * Get container based on id.
   * @param {String} id - Modal container id.
   */


  function getContainerById(id) {
    if (typeof id !== 'string') {
      throw new Error('Must be a valid string id');
    }

    return document.getElementById(id);
  }
  /**
   * Expose public interface.
   */


  return Object.freeze({
    init,
    show,
    close
  });
});